<template>
    <div class="q-px-md q-py-md" v-if="!dense">
        <q-linear-progress size="20px" :value="fixedFloat(remaining / 100)" color="primary" :animation-speed="200">
            <div class="absolute-full flex flex-center">
                <q-badge color="white" text-color="primary" :label="remaining.toFixed(2) + '%'" />
            </div>
        </q-linear-progress>
    </div>

    <q-item :dense="dense">
        <q-item-section>
            <q-linear-progress v-if="dense" size="20px" :value="fixedFloat(remaining / 100)" color="primary" :animation-speed="200">
                <div class="absolute-full flex flex-center">
                    <q-badge color="white" text-color="primary" :label="remaining.toFixed(2) + '%'" />
                </div>
            </q-linear-progress>
        </q-item-section>
        <q-item-section side>
            <q-btn
                :dense="dense"
                flat
                no-caps
                icon="mdi-cancel"
                label="Reset"
                color="negative"
                @click="resetSplits()"
            />
        </q-item-section>
        <q-item-section side>
            <q-btn
                :dense="dense"
                flat
                no-caps
                icon="mdi-chart-pie"
                label="Split Evenly"
                color="primary"
                @click="handleEvenSplit()"
            />
        </q-item-section>
    </q-item>

    <q-list :dense="dense" :separator="$q.dark.isActive">
        <q-item v-for="({worker, split}, i) in pay" :class="{ 'bg-grey-2': i % 2 === 0 && !$q.dark.isActive }">
            <q-item-section side>
                <q-btn
                    dense
                    :outline="$q.dark.isActive"
                    size="sm"
                    :flat="!$q.dark.isActive"
                    icon="mdi-delete"
                    color="negative"
                    @click="handleCheckbox(false, worker)"
                />
            </q-item-section>
            <q-item-section>
                <q-item-label>
                    <Avatar size="sm" :user="worker" :contact="false" class="q-mr-sm" />
                    <span class="text-underline cursor-pointer">
                        {{ worker.name ?? 'N/A' }}
                        <span class="text-bold">({{ parseFloat(split) }}%)</span>
                        <q-popup-edit 
                            auto-save
                            :model-value="pay[i].split" 
                            @update:model-value="$event => handleInput(i, $event)"
                        >
                            <template v-slot="scope">
                                <q-item dense>
                                    <q-item-section>
                                        <q-item-label class="text-center">
                                            <Avatar size="sm" :user="worker" :contact="false" />
                                            {{ worker.name ?? 'N/A' }}
                                        </q-item-label>
                                    </q-item-section>
                                </q-item>
                                <q-input
                                    label="Worker Split"
                                    v-model="scope.value"
                                    dense
                                    autofocus
                                    clearable
                                    @clear="scope.value = 0"
                                    @keyup.enter="scope.set"
                                    type="number"
                                    :rules="[val => val <= fixedFloat(parseFloat(pay[i].split) + parseFloat(remaining)) || 'Worker split cannot be greater than ' + parseFloat(pay[i].split + remaining).toFixed(2) + '%', val => val >= 0 || 'Worker split cannot be less than 0%']"
                                    :hint="`Remaining: ${parseFloat(pay[i].split - scope.value).toFixed(2)}% | Max: ${parseFloat(parseFloat(pay[i].split) + parseFloat(remaining)).toFixed(2)}%`"
                                >
                                    <template #append>
                                        <q-icon name="mdi-percent" />
                                    </template>
                                    <template #after>
                                        <q-btn
                                            padding="5px 7px"
                                            icon="mdi-decimal"
                                            color="primary"
                                            @click="scope.value = fixedFloat((scope.value + '').split('.')[0] + '.00')"
                                        >
                                            <q-tooltip>
                                                Zero out decimal
                                            </q-tooltip>
                                        </q-btn>
                                    </template>
                                </q-input>
                                
                                <q-item>
                                    <q-item-section v-for="i in ['-.01', '-.1', '-1', '1', '.1', '.01']">
                                        <!-- -1, -0.1, -0.01 butons -->
                                        <q-btn
                                            padding="1px 3px"
                                            :color="parseFloat(i) < 0 ? 'negative' : 'primary'"
                                            :label="parseFloat(i) > 0 ? '+' + i : i"
                                            @click="scope.value = fixedFloat(parseFloat(scope.value) + parseFloat(i))"
                                        />
                                    </q-item-section>
                                </q-item>
                                
                                <q-item dense>
                                    <q-item-section class="text-center">
                                        <q-btn
                                            size="sm"
                                            color="primary"
                                            label="Fill Remaining"
                                            @click="scope.value = parseFloat(parseFloat(pay[i].split) + parseFloat(remaining)).toFixed(2)"
                                        />
                                    </q-item-section>
                                </q-item>
                            </template>
                        </q-popup-edit>
                    </span>
                </q-item-label>
            </q-item-section>
            <q-item-section class="q-pr-xs">
                <q-slider
                    :model-value="split"
                    @update:model-value="val => { handleSlider(i, val); }"
                    :min="0"
                    :max="100"
                    :inner-max="parseFloat(split) + parseFloat(remaining)"
                    :step="0.01"
                    color="primary"
                    label
                />
            </q-item-section>
            <q-item-section side>
                <q-item-label>
                    <span class="cursor-pointer text-center bg-grey-9 q-mr-sm q-pa-xs" style="min-width: 72px;">
                        <q-icon name="mdi-timer-stop-outline" />
                        {{ to12Hour(pay.time_start) }}
                        <q-tooltip>
                            Start Time {{ pay.time_start }}
                        </q-tooltip>
                        <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                            <q-time
                                now-btn
                                v-model="pay.time_start"
                                :landscape="$q.screen.gt.xs"
                            >
                                <div class="row items-center justify-end">
                                    <q-btn v-close-popup label="Close" color="primary" flat />
                                </div>
                            </q-time>
                        </q-popup-proxy>
                    </span>
                    <span class="cursor-pointer text-center bg-grey-9 q-pa-xs" style="min-width: 72px;">
                        <q-icon name="mdi-timer-play-outline" />
                        {{ to12Hour(pay.time_end) }}
                        <q-tooltip>
                            End Time {{ pay.time_end }}
                        </q-tooltip>
                        <q-popup-proxy cover transition-show="scale" transition-hide="scale">
                            <q-time
                                now-btn
                                v-model="pay.time_end"
                                :landscape="$q.screen.gt.xs"
                            >
                                <div class="row items-center justify-end">
                                    <q-btn v-close-popup label="Close" color="primary" flat />
                                </div>
                            </q-time>
                        </q-popup-proxy>
                    </span>
                </q-item-label>
            </q-item-section>
        </q-item>
    </q-list>
</template>

<script>
import Avatar from '@/Components/Avatar.vue';

export default {
    name: 'LumperSplitModel',
    emits: [
        'update:pay',
        'update:total',
        'update:remaining',
        'delete'
    ],
    components: {
        Avatar,
    },
    props: {
        dense: {
            type: Boolean,
            default: false,
        },
        pay: {
            type: Array,
            required: true
        },
        emitDelete: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            total: 0,
            lastSum: 0,
            remaining: 0,
            errors: {
                timeclock: [],
            }
        }
    },
    methods: {
        resetSplits() {
            if (!window.confirm('Are you sure you want to reset all worker splits?')) return;

            let temp = [...this.pay];

            temp.forEach(l => {
                l.split = 0;
            });

            this.total = 0;
            this.lastSum = 0;
            this.remaining = 100;

            this.$emit('update:pay', temp);
        },
        isSelected(user) {
            return this.pay
                .some(l => l.labor_id === user.id);
        },
        handleSums() {
            this.total = this.splitSum();
            this.lastSum = this.splitSum();
            this.remaining = 100 - this.splitSum();

            this.$emit('update:total', this.total);
            this.$emit('update:remaining', this.remaining);
        },
        handleCheckbox(val, worker) {
            let temp = [...this.pay];

            if (!val) {
                if (this.emitDelete) {
                    this.$emit('delete', worker.id);
                } else {
                    temp = temp.filter(l => l.labor_id !== worker.id);
                }
            } else {
                temp.push({
                    worker: worker,
                    labor_id: worker.id,
                    pay: 0.0,
                    split: 0.0,
                });
            }

            this.$emit('update:pay', temp);
        },
        handleInput(index, percent) {
            const realRemaining = percent - this.pay[index].split;
            // Check if the percent is greater than the remaining
            if (realRemaining > this.remaining) {
                this.$q.notify({
                    message: `Worker split cannot be greater than ${this.remaining.toFixed(2)}%`,
                    color: 'negative',
                    icon: 'mdi-alert-circle-outline',
                    position: 'top',
                    timeout: 2000,
                });
                return;
            }
            // Handle remaining
            this.handleSlider(index, percent);
        },
        handleSlider(index, percent) {
            let temp = [...this.pay];
            
            temp[index].split = this.fixedFloat(percent);

            this.$emit('update:pay', temp);
        },
        handleEvenSplit() {
            if (!window.confirm('Are you sure you want to split evenly?')) return;

            let total = 0;
            let remainder = 100;

            let temp = [...this.pay];

            temp.forEach(l => {
                let split = this.fixedFloat(100 / temp.length);
                remainder = this.fixedFloat(100 - total);

                if (remainder < split) {
                    split = remainder;
                }

                if (total > 100) {
                    split = 0;
                }

                l.split = split;
                total += split;
            });
            // Check if there's any remaining
            if (total < 100) {
                // Add to the first lumper
                temp[0].split = this.fixedFloat(
                    temp[0].split + this.fixedFloat(100 - total)
                );
            }

            this.$emit('update:pay', temp);
        },
        splitSum() {
            return this.fixedFloat(
                this.pay
                    .map(l => this.fixedFloat(l.split))
                    .reduce((a, b) => parseFloat(a) + parseFloat(b), 0)
            );
        },
        to12Hour(time) {
            if (!time) return 'NOT SET';

            return this.formatTo('do h:mm a', time);
        },
    },
    watch: {
        pay: {
            handler() {
                this.handleSums();
            },
            deep: true,
            immediate: true,
        },
    },
}
</script>